import type {ChangeEvent} from "react";
import {useCallback, useEffect, useMemo, useState} from "react";

import colors from "styles/themes.module.scss";
import {GeneralButton, SearchInput} from "mapx-components";
import {ModalContent, ModalHeader, ModalSeparator} from "components";
import {useAppDispatch, useAppSelector, useLockBodyScroll, useModalHook} from "hooks";
import {
	addFromMarketMapModalPropsSelector,
	addFromMarketMapModalSelector,
	marketMapModalDisplayedForMRSelector,
	marketMapsModalSelector,
} from "store/market-map/marketMapSelectors";
import {addFromMarketMapModal} from "store/market-map/marketMapActions";
import {addCompaniesFromMarketMapToTargetList} from "store/target-list/targetListAsyncActions";
import {addingFromMarketMapInProgressSelector} from "store/target-list/targetListSelectors";
import type {STMarketMap} from "api/marketMapApi/types";

import styles from "./addFromMarketMapModal.module.scss";
import List from "./list";
import {mRAddCompaniesToCompaniesSelectedList} from "store/market-report/marketReportFormAsyncActions";

const AddFromMarketMapModal = () => {
	const dispatch = useAppDispatch();

	const modalDisplay = useAppSelector(addFromMarketMapModalSelector);

	const marketMaps: STMarketMap[] = useAppSelector(marketMapsModalSelector);

	const submitting = useAppSelector(addingFromMarketMapInProgressSelector);

	const {targetListID} = useAppSelector(addFromMarketMapModalPropsSelector);

	const marketMapForMR = useAppSelector(marketMapModalDisplayedForMRSelector);

	const [search, setSearch] = useState("");
	const [selected, setSelected] = useState<number | null>(null);

	const {modalIsOpen, customStyles, Modal} = useModalHook(
		{content: {width: "99%", borderRadius: "8px", maxWidth: "720px"}},
		modalDisplay,
		colors.mainThemeColor,
	);

	const selectedMarketMap = useMemo(() => {
		return marketMaps.find((map) => map.id === selected);
	}, [marketMaps, selected]);

	const handleModalHide = useCallback(() => {
		dispatch(addFromMarketMapModal(false, {}));
	}, [dispatch]);

	const handleOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setSearch(e.target.value);
		},
		[setSearch],
	);

	const handleSubmit = useCallback(async () => {
		if (selectedMarketMap) {
			if (!marketMapForMR) {
				await dispatch(
					addCompaniesFromMarketMapToTargetList(selectedMarketMap.id, targetListID),
				);
			} else {
				dispatch(mRAddCompaniesToCompaniesSelectedList(selectedMarketMap.companies));
				dispatch(addFromMarketMapModal(false, {}));
			}
		}
	}, [dispatch, marketMapForMR, selectedMarketMap, targetListID]);

	useEffect(() => {
		if (!modalDisplay) {
			setSearch("");
		}
	}, [modalDisplay]);

	useLockBodyScroll(modalIsOpen);

	return (
		<Modal
			title="Add from Target List"
			style={customStyles}
			isOpen={modalIsOpen}
			contentLabel="Modal"
			onRequestClose={handleModalHide}
		>
			<ModalHeader title="Add from Target List" onClose={handleModalHide} />
			<ModalSeparator />
			<ModalContent padding="16px" className={styles.content}>
				{/* @TODO: Convert SearchInput into TS */}
				{/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
				{/* @ts-ignore */}
				<SearchInput
					autoComplete="off"
					customClass={styles.search}
					onChange={handleOnChange}
					placeholder="Search a Target List"
					type="text"
					value={search}
					margin="0"
				/>
				<List selected={selected} setSelected={setSelected} search={search} />
				<GeneralButton
					customClass={styles.button}
					disabled={!selectedMarketMap?.companies?.length}
					loadingSave={submitting}
					title="Add from Target List"
					type="button"
					onClick={handleSubmit}
				/>
			</ModalContent>
		</Modal>
	);
};

export default AddFromMarketMapModal;
